// src/pages/SmartSensorDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { 
    FaArrowLeft, FaSyncAlt, FaTemperatureHigh, FaTint, FaWind, 
    FaCloudSun, FaThermometerHalf, FaFlask, FaWater, FaSmog, FaWifi,
    FaChartLine // Tambahkan FaCalendarAlt karena sebelumnya sudah diimpor
} from 'react-icons/fa'; 
import '../styles/SmartSensorDetail.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const getSensorIcon = (sensorType) => {
    switch (sensorType) {
        case 'temperature_DHT11': return <FaTemperatureHigh className="sensor-icon" />;
        case 'humidity_DHT11': return <FaTint className="sensor-icon" />;
        case 'pressure_BMP180': return <FaWind className="sensor-icon" />;
        case 'soil_moisture': return <FaTint className="sensor-icon" />;
        case 'gas_mq': return <FaSmog className="sensor-icon" />;
        case 'vibration_sensor': return <FaWifi className="sensor-icon" />;
        case 'temp_ds18b20': return <FaThermometerHalf className="sensor-icon" />;
        case 'ph_sensor': return <FaFlask className="sensor-icon" />;
        case 'tds_sensor': return <FaWater className="sensor-icon" />;
        case 'salinity_sensor': return <FaWater className="sensor-icon" />;
        case 'turbidity_sensor': return <FaSmog className="sensor-icon" />;
        default: return <FaCloudSun className="sensor-icon" />;
    }
};

const getSensorUnitAndDisplay = (sensorType, value) => {
    let unit = '';
    let displayValue = value;
    let label = '';
    switch (sensorType) {
        case 'temperature_DHT11':
        case 'temp_ds18b20': unit = '°C'; label = 'Suhu'; break;
        case 'humidity_DHT11':
        case 'soil_moisture': unit = '%'; label = 'Kelembaban'; break;
        case 'pressure_BMP180': unit = 'hPa'; label = 'Tekanan'; break;
        case 'gas_mq': unit = 'ppm'; label = 'Gas'; break;
        case 'ph_sensor': unit = 'pH'; label = 'pH Air'; displayValue = typeof value === 'number' ? value.toFixed(2) : value; break;
        case 'tds_sensor': unit = 'ppm'; label = 'TDS Air'; break;
        case 'salinity_sensor': unit = 'ppt'; label = 'Kadar Garam'; break;
        case 'turbidity_sensor': unit = 'NTU'; label = 'Kekeruhan'; break;
        case 'voltage': unit = 'V'; label = 'Tegangan'; displayValue = typeof value === 'number' ? value.toFixed(2) : value; break; // Tambahkan voltage
        case 'light_sensor': unit = 'lux'; label = 'Cahaya'; break; // Tambahkan light_sensor
        default: unit = ''; label = 'Nilai'; break;
    }
    return { label, displayValue: `${displayValue}${unit}`, rawValue: value };
};

const SmartSensorDetail = () => {
    const { sensorId } = useParams();
    const navigate = useNavigate();
    const [sensorDetails, setSensorDetails] = useState(null);
    const [readings, setReadings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Pastikan token diambil setiap kali komponen di-render ulang
    const token = localStorage.getItem('token'); 

    // State untuk kontrol histori grafik
    const [timeRange, setTimeRange] = useState('all'); // 'all', 'weekly', 'monthly', 'by_month'
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const redirectToLogin = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('is_super_admin');
        navigate('/login', { replace: true });
        alert('Sesi Anda telah berakhir atau tidak valid. Silakan login kembali.');
    }, [navigate]);


    const fetchSensorData = useCallback(async () => {
        setLoading(true);
        setError('');
        
        // ✅ Cek token di awal fungsi fetch data
        if (!token) {
            console.warn("No token found when trying to fetch sensor data.");
            redirectToLogin();
            return;
        }

        try {
            // Ambil detail sensor (config)
            const detailsRes = await axios.get(`https://depotaircerdas.com/api/superadmin/tables/sensors/${sensorId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSensorDetails(detailsRes.data[0]);

            // Ambil pembacaan historis berdasarkan timeRange
            let readingsUrl = `https://depotaircerdas.com/api/superadmin/tables/sensors/${sensorId}/readings?_limit=1000`;

            if (timeRange === 'weekly') {
                readingsUrl += `&_range=weekly`;
            } else if (timeRange === 'monthly') {
                readingsUrl += `&_range=monthly`;
            } else if (timeRange === 'by_month') {
                readingsUrl += `&_range=by_month&_month=${selectedMonth}&_year=${selectedYear}`;
            }

            const readingsRes = await axios.get(readingsUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReadings(Array.isArray(readingsRes.data) ? readingsRes.data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)) : []);
            
        } catch (err) {
            console.error(`Error fetching sensor ${sensorId} details:`, err);
            // ✅ Penanganan error 401/403 yang lebih spesifik
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                redirectToLogin();
            } else {
                setError('Gagal memuat detail sensor. ' + (err.response?.data?.message || err.message));
            }
            setSensorDetails(null);
            setReadings([]);
        } finally {
            setLoading(false);
        }
    }, [sensorId, token, timeRange, selectedMonth, selectedYear, redirectToLogin]);


    useEffect(() => {
        // ✅ Pengecekan token juga di useEffect mount
        if (!token) {
            redirectToLogin();
            return;
        }
        if (sensorId) {
            fetchSensorData();
        }
    }, [sensorId, token, fetchSensorData, redirectToLogin]); // Hapus navigate dari dependency array jika pakai useCallback


    // Handle perubahan pilihan range waktu
    const handleTimeRangeChange = (e) => {
        setTimeRange(e.target.value);
    };

    // Handle perubahan bulan/tahun
    const handleMonthChange = (e) => {
        setSelectedMonth(parseInt(e.target.value));
    };

    const handleYearChange = (e) => {
        setSelectedYear(parseInt(e.target.value));
    };

    if (loading) return <div className="sensor-detail-loading">Memuat detail sensor...</div>;
    if (error) return <div className="sensor-detail-error-message">{error}</div>;
    if (!sensorDetails) return <div className="sensor-detail-not-found">Sensor tidak ditemukan. Pastikan ID sensor valid.</div>;

    // Data untuk Chart.js
    const { label: chartLabel } = getSensorUnitAndDisplay(sensorDetails.sensor_type, 0);
    const chartData = {
        labels: readings.map(r => new Date(r.timestamp).toLocaleString()),
        datasets: [
            {
                label: `${chartLabel} (${sensorDetails.sensor_name})`,
                data: readings.map(r => r.value),
                borderColor: '#007bff',
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointBackgroundColor: '#007bff',
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: '#333',
                }
            },
            title: {
                display: true,
                text: 'Pembacaan Sensor Historis',
                color: '#333',
                font: {
                    size: 18,
                    weight: 'bold'
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const { label: unitLabel, displayValue } = getSensorUnitAndDisplay(sensorDetails.sensor_type, context.raw);
                        return `${unitLabel}: ${displayValue}`;
                    }
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Waktu Pembacaan',
                    color: '#555',
                    font: { size: 14 }
                },
                ticks: {
                    color: '#666',
                },
                grid: {
                    color: 'rgba(0,0,0,0.05)',
                }
            },
            y: {
                title: {
                    display: true,
                    text: `Nilai ${chartLabel}`,
                    color: '#555',
                    font: { size: 14 }
                },
                ticks: {
                    color: '#666',
                },
                grid: {
                    color: 'rgba(0,0,0,0.05)',
                }
            }
        }
    };

    // Data untuk dropdown bulan/tahun
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i); // 5 tahun terakhir
    const months = [
        { value: 1, label: 'Januari' }, { value: 2, label: 'Februari' }, { value: 3, label: 'Maret' },
        { value: 4, label: 'April' }, { value: 5, label: 'Mei' }, { value: 6, label: 'Juni' },
        { value: 7, label: 'Juli' }, { value: 8, label: 'Agustus' }, { value: 9, label: 'September' },
        { value: 10, label: 'Oktober' }, { value: 11, label: 'November' }, { value: 12, label: 'Desember' },
    ];

    return (
        <div className="smart-sensor-detail-container">
            <button onClick={() => navigate(-1)} className="back-btn"><FaArrowLeft /> Kembali ke Dashboard</button>
            <h2>Detail Sensor: {sensorDetails.sensor_name} {getSensorIcon(sensorDetails.sensor_type)}</h2>
            <p className="intro-text">Informasi lengkap dan riwayat pembacaan untuk sensor {sensorDetails.sensor_name} (Perangkat: {sensorDetails.device_name}).</p>

            <div className="sensor-info-card">
                <h3>Informasi Dasar Sensor</h3>
                <div className="info-grid">
                    <p><strong>ID Sensor:</strong> {sensorDetails.id}</p>
                    <p><strong>Tipe Sensor:</strong> {sensorDetails.sensor_type}</p>
                    <p><strong>Nama Sensor:</strong> {sensorDetails.sensor_name}</p>
                    <p><strong>Perangkat:</strong> {sensorDetails.device_name} (ID: {sensorDetails.device_id})</p>
                    <p><strong>Lokasi Perangkat:</strong> {sensorDetails.location || '-'}</p>
                    <p><strong>Pemilik Perangkat:</strong> {sensorDetails.device_owner_username}</p>
                    <p><strong>Nilai Min/Max:</strong> {sensorDetails.min_value ?? 'N/A'} / {sensorDetails.max_value ?? 'N/A'}</p>
                    <p><strong>Status Aktif:</strong> {sensorDetails.is_active === 1 ? 'Ya' : 'Tidak'}</p>
                    <p><strong>Terakhir Aktif:</strong> {sensorDetails.last_active_at ? new Date(sensorDetails.last_active_at).toLocaleString() : '-'}</p>
                </div>
            </div>

            <div className="sensor-readings-card">
                <h3>Pembacaan Historis <FaChartLine /></h3>
                <div className="chart-controls">
                    <label htmlFor="time-range-select">Tampilkan Data:</label>
                    <select id="time-range-select" value={timeRange} onChange={handleTimeRangeChange}>
                        <option value="all">Semua Data</option>
                        <option value="weekly">1 Minggu Terakhir</option>
                        <option value="monthly">1 Bulan Terakhir</option>
                        <option value="by_month">Per Bulan Spesifik</option>
                    </select>

                    {timeRange === 'by_month' && (
                        <>
                            <select value={selectedMonth} onChange={handleMonthChange}>
                                {months.map(month => (
                                    <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                            </select>
                            <select value={selectedYear} onChange={handleYearChange}>
                                {years.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </>
                    )}
                    <button onClick={fetchSensorData} className="refresh-readings-btn"><FaSyncAlt /> Refresh</button>
                </div>

                {readings.length === 0 ? (
                    <p className="no-readings-message">Tidak ada data pembacaan historis untuk periode ini.</p>
                ) : (
                    <div className="chart-container" style={{ height: '400px' }}>
                        <Line data={chartData} options={chartOptions} />
                    </div>
                )}
            </div>

            {readings.length > 0 && (
                <div className="raw-readings-table-card">
                    <h3>Data Pembacaan Mentah (Terbaru 100)</h3>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Timestamp</th>
                                    <th>Nilai</th>
                                    <th>Tipe Sensor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {readings.slice(-100).reverse().map((r, index) => (
                                    <tr key={index}>
                                        <td>{new Date(r.timestamp).toLocaleString()}</td>
                                        <td>{getSensorUnitAndDisplay(sensorDetails.sensor_type, r.value).displayValue}</td>
                                        <td>{sensorDetails.sensor_name} ({sensorDetails.sensor_type})</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SmartSensorDetail;